package diana.soleil.design.iraniancanadienbazzar.core.firebase

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.storage.Data
import dev.gitlive.firebase.storage.storage
import dev.gitlive.firebase.storage.storageMetadata

/**
 * Wraps raw image [bytes] in GitLive's platform [Data] holder. GitLive has no common `Data(ByteArray)`
 * constructor — Android wraps a `ByteArray`, JS a `Uint8Array`, iOS an `NSData` — so this is the one
 * per-platform bridge the upload needs.
 */
internal expect fun storageDataOf(bytes: ByteArray): Data

/** Longest image side uploaded; larger picks are downscaled. Serving cost scales with this². */
private const val MAX_UPLOAD_DIMENSION_PX = 1600
private const val UPLOAD_JPEG_QUALITY = 82

/**
 * Uploads image [bytes] to Cloud Storage at [path] and returns the public download URL. Storage rules
 * (see `firebase/storage.rules`) gate writes to the owner of the referenced entity, so [path] must be
 * under a path the signed-in user owns (e.g. `marketplace/{itemId}` for that item's seller).
 *
 * Before upload the image is downscaled to [MAX_UPLOAD_DIMENSION_PX] and re-encoded as JPEG — Storage
 * download bandwidth is the dominant Firebase cost, so full-resolution camera bytes must never be
 * stored. Originals are kept only when they cannot be decoded or are already smaller than the
 * re-encode; animated GIFs are kept as-is (re-encoding would drop the animation).
 */
suspend fun uploadImageToStorage(path: String, bytes: ByteArray, contentType: String): String {
    val compressed = if (contentType.equals("image/gif", ignoreCase = true)) {
        null
    } else {
        downscaleImage(bytes, MAX_UPLOAD_DIMENSION_PX, UPLOAD_JPEG_QUALITY)
            ?.takeIf { it.bytes.size < bytes.size }
    }
    val uploadBytes = compressed?.bytes ?: bytes
    val uploadType = compressed?.contentType ?: contentType
    val ref = Firebase.storage.reference(path)
    ref.putData(storageDataOf(uploadBytes), storageMetadata { this.contentType = uploadType })
    return ref.getDownloadUrl()
}
